import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IParentHomeworkDetailResponse,
  IParentHomeworkFiltersResponse,
  IParentHomeworkListResponse,
  IParentHomeworkPagination,
  IParentHomeworkState,
} from '../../interface/parentHomework.interface';
import {
  fetchParentHomeworkDetail,
  fetchParentHomeworkFilters,
  fetchParentHomeworkList,
  submitParentHomework,
} from '../action/parentHomework.action';

const initialPagination: IParentHomeworkPagination = {
  page: 1,
  pageSize: 5,
  totalRecords: 0,
  totalPages: 0,
};

const initialState: IParentHomeworkState = {
  homeworks: [],
  pagination: initialPagination,
  listLoading: false,
  listError: null,
  teacherOptions: [],
  subjectOptions: [],
  filtersLoading: false,
  detail: null,
  detailLoading: false,
  detailError: null,
  submitLoading: false,
  submitError: null,
};

const parentHomeworkSlice = createSlice({
  name: 'parentHomework',
  initialState,
  reducers: {
    // Pages dispatch this on mount (and on child switch) for a clean state.
    resetParentHomeworkState: () => initialState,
    clearParentHomeworkDetail: (state) => {
      state.detail = null;
      state.detailError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchParentHomeworkList.pending, (state) => {
        state.listLoading = true;
        state.listError = null;
      })
      .addCase(
        fetchParentHomeworkList.fulfilled,
        (state, action: PayloadAction<IParentHomeworkListResponse>) => {
          state.listLoading = false;
          state.homeworks = action.payload.data.homeworks;
          state.pagination = action.payload.data.pagination;
          state.listError = null;
        },
      )
      .addCase(fetchParentHomeworkList.rejected, (state, action) => {
        state.listLoading = false;
        state.homeworks = [];
        state.pagination = initialPagination;
        state.listError = action.payload?.message || 'Failed to fetch homework.';
      })
      .addCase(fetchParentHomeworkFilters.pending, (state) => {
        state.filtersLoading = true;
      })
      .addCase(
        fetchParentHomeworkFilters.fulfilled,
        (state, action: PayloadAction<IParentHomeworkFiltersResponse>) => {
          state.filtersLoading = false;
          state.teacherOptions = action.payload.data.teachers || [];
          state.subjectOptions = action.payload.data.subjects || [];
        },
      )
      .addCase(fetchParentHomeworkFilters.rejected, (state) => {
        state.filtersLoading = false;
        state.teacherOptions = [];
        state.subjectOptions = [];
      })
      .addCase(fetchParentHomeworkDetail.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(
        fetchParentHomeworkDetail.fulfilled,
        (state, action: PayloadAction<IParentHomeworkDetailResponse>) => {
          state.detailLoading = false;
          state.detail = action.payload.data;
          state.detailError = null;
        },
      )
      .addCase(fetchParentHomeworkDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.detail = null;
        state.detailError = action.payload?.message || 'Failed to load homework.';
      })
      .addCase(submitParentHomework.pending, (state) => {
        state.submitLoading = true;
        state.submitError = null;
      })
      .addCase(submitParentHomework.fulfilled, (state) => {
        state.submitLoading = false;
        state.submitError = null;
      })
      .addCase(submitParentHomework.rejected, (state, action) => {
        state.submitLoading = false;
        state.submitError = action.payload?.message || 'Failed to submit homework.';
      });
  },
});

export const { resetParentHomeworkState, clearParentHomeworkDetail } = parentHomeworkSlice.actions;

export default parentHomeworkSlice.reducer;
